// components/consumers/ConsumerDetail.tsx

import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tabs } from "@shared/ui/tabs";
import {
  Activity,
  AlertCircle,
  Banknote,
  Crown,
  File,
  Mail,
  MessageSquareIcon,
  Pencil,
  Plug,
  Send,
  Settings,
  User,
  Phone,
  MapPin,
  ArrowLeft,
} from "lucide-react";
import { SearchInput } from "@shared/components/SearchInput";
// Import the new componentized tabs
import { ComplaintsTab } from "./components/ComplaintsTab";
import { ConnectionsTab } from "./components/connection/ConnectionsTab";
import { DocumentsTab } from "./components/DocumentsTab";
import { FinancialTab } from "./components/FinancialTab";
import { OverviewLanding } from "./components/overview/landing";
import { RequestsTab } from "./components/RequestsTab";
import { ServiceTab } from "./components/ServiceTab";

// Import API service and types
import { Consumer } from "@features/cx/consumerManagement/types";
import { consumerApi } from "../../api";
import { RequestCommunications } from "@features/cx/shared/components/communicationLandingTab";
import { NotesTab } from "@features/cx/shared/components/tabs/NotesTab";
import { TimelineTab } from "@features/cx/shared/components/tabs/TimelineTab";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCity,
  useConsumerRelation,
  useConsumerWithBillData,
  useEditActiveConsumer,
  useSearchConsumers,
  useSubAccountsList,
  useContract,
} from "../../hooks";
import { QuickActionMenu } from "./components/QuickActionMenu";
import { ConnectionLanding } from "./components/connection/ConnectionLanding";
import { formatTerritoryAddress } from "@shared/utils/address";
import { Button } from "@shared/ui/button";
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription } from "@shared/ui/dialog";
import { FormField, FormService } from "@shared/services/FormServices";
import { fi } from "zod/dist/types/v4/locales";
import { DynamicForm } from "@shared/components/DynamicForm";
import MaskedSSN from "@features/cx/shared/components/MaskedSSN";
import { BUTTON_FIELD_CLASSES, FIELD_CLASSES } from "@shared/utils/constants";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { ConsumerInfoCard } from "./components/ConsumerInfoCard";
import { QuickActions } from "./components/QuickActions";
import {
  useCategory,
  useOwnerType,
  usePlans,
  useSubCategory,
  useUtilityServices,
} from "@features/cx/consumerOnboarding/hooks";
import SubAccountTab from "./components/subAccountTab";
import DocumentLandingTab from "./components/documentLandingTab";
import { CXCxOnboardingApi } from "@features/cx/consumer-onboarding/api";
import { toast } from "@shared/hooks/use-toast";
import { Search } from "lucide-react";
import TransferAlert from "./components/TransferAlert";
import { PageHeader } from "@shared/ui/pageHeader";

const ConsumerDetail = () => {
  const { consumerNo } = useParams<{ consumerNo: string }>();
  const remoteUtilityId = useRemoteUtilityId();
  const [updateProfileOpen, setUpdateProfileOpen] = useState<boolean>(false);
  const {
    data: consumerInfo,
    isLoading: loading,
    error,
    refetch: fetchConsumerData,
  } = useConsumerWithBillData(consumerNo!, remoteUtilityId);
  const navigate = useNavigate();

  const consumerID = consumerInfo?.id?.toString() || "";
  console.log("details.tsx - consumerInfo:", consumerInfo);
  console.log("details.tsx - consumerID:", consumerID);
  const { data: subAccountData } = useSubAccountsList({
    page: 1,
    limit: 50,
    activeConsumers: 1,
    remoteUtilityId: remoteUtilityId.toString(),
    isSubAccount: 1,
    consumerId: consumerID,
  });
  const contractIds = useMemo(() => {
    const ids: any[] = [];
    const planContract =
      consumerInfo?.planDetails?.contract ||
      consumerInfo?.plan_details?.contract;
    if (planContract) {
      ids.push(planContract);
    }
    const remoteContractIds =
      consumerInfo?.additionalData?.remoteContractId ||
      consumerInfo?.additional_data?.remote_contract_id;
    if (Array.isArray(remoteContractIds)) {
      remoteContractIds.forEach((id) => {
        if (id && !ids.includes(id)) {
          ids.push(id);
        }
      });
    } else if (remoteContractIds) {
      if (!ids.includes(remoteContractIds)) {
        ids.push(remoteContractIds);
      }
    }
    return ids;
  }, [consumerInfo]);

  const { data: contractData } = useContract(remoteUtilityId, contractIds);
  const subCategoryCode = consumerInfo?.subCategory || "";
  const tabComponents = {
    overview: {
      label: "Overview",
      icon: <User className="h-4 w-4" />,
      component: (
        <OverviewLanding
          consumer={consumerInfo}
          consumerId={consumerInfo?.id.toString()}
          consumerNo={consumerNo}
          remoteUtilityId={Number(remoteUtilityId)}
        />
      ),
    },
    ...(consumerInfo?.ownershipDisplay === "Master"
      ? {
          subAccount: {
            label: "Sub Account",
            icon: <User className="h-4 w-4" />,
            component: (
              <SubAccountTab
                subAccountData={subAccountData}
                consumerId={consumerID}
                subCategory={subCategoryCode}
                contractData={contractData}
              />
            ),
          },
        }
      : {}),
    financial: {
      label: "Financial",
      icon: <Banknote className="h-4 w-4" />,
      component: (
        <FinancialTab
          consumerId={consumerInfo?.id.toString()}
          consumerNo={consumerNo}
          remoteUtilityId={remoteUtilityId}
        />
      ),
    },
    connections: {
      label: "Connections",
      icon: <Plug className="h-4 w-4" />,
      component: (
        <ConnectionLanding
          consumerNo={consumerNo}
          consumerID={consumerID}
          remoteUtilityId={remoteUtilityId}
        />
      ),
    },
    service: {
      label: "Service",
      icon: <Settings className="h-4 w-4" />,
      component: (
        <ServiceTab
          consumerId={consumerInfo?.id.toString()}
          consumerNo={consumerNo}
          remoteUtilityId={Number(remoteUtilityId)}
        />
      ),
    },
    complaints: {
      label: "Complaint History",
      icon: <AlertCircle className="h-4 w-4" />,
      component: (
        <ComplaintsTab
          consumerId={consumerInfo?.id.toString()}
          remoteUtilityId={Number(remoteUtilityId)}
        />
      ),
    },
    requests: {
      label: "Request History",
      icon: <Send className="h-4 w-4" />,
      component: (
        <RequestsTab
          consumerId={consumerInfo?.id.toString()}
          remoteUtilityId={Number(remoteUtilityId)}
        />
      ),
    },
    communication: {
      label: "Communication",
      icon: <MessageSquareIcon className="h-4 w-4" />,
      subTabsListClassName: "grid w-full grid-cols-3",
      component: (
        <RequestCommunications
          entityId={consumerInfo?.id.toString()}
          remoteUtilityId={Number(remoteUtilityId)}
          email={consumerInfo?.email}
          phone={consumerInfo?.contactNumber || ""}
          action="consumer"
        />
      ),
    },
    documents: {
      label: "Documents",
      icon: <File className="h-4 w-4" />,
      component: (
        <DocumentLandingTab
          consumerId={consumerInfo?.id.toString()}
          consumerNo={consumerNo}
          remoteUtilityId={Number(remoteUtilityId)}
          subCategory={subCategoryCode}
          contractData={contractData}
        />
      ),
    },
    notes: {
      label: "Notes",
      icon: <Pencil className="h-4 w-4" />,
      component: (
        <NotesTab
          requestId={consumerInfo?.id.toString()}
          remoteUtilityId={Number(remoteUtilityId)}
          source="consumer"
        />
      ),
    },
    activity: {
      label: "Activity",
      icon: <Activity className="h-4 w-4" />,
      component: (
        <TimelineTab
          remoteUtilityId={Number(remoteUtilityId)}
          entityId={consumerInfo?.id.toString()}
          actionType="nsc"
        />
      ),
    },
  };

  const urlMapping = {
    overview: "overview",
    ...(consumerInfo?.ownershipDisplay === "Master"
      ? { subAccount: "subAccount" }
      : {}),
    financial: "financial",
    connections: "connections",
    service: "service",
    complaints: "complaints",
    requests: "requests",
    communication: "communication",
    documents: "documents",
    notes: "notes",
    activity: "activity",
  };

  return (
    <div className="space-y-6">
      {/* Header with PageHeader */}
      <div className="flex items-center justify-between">
        <div>
          <PageHeader
            title="Consumer Profile"
            description="Manage consumer account information and services"
            onBack={() => navigate("/cx/consumer-management/")}
          />
        </div>
      </div>

      {consumerInfo?.isTransferred &&
        (consumerInfo?.ownershipDisplay?.toLowerCase() === "tenant" ||
          consumerInfo?.ownershipDisplay?.toLowerCase() === "sub-account") && (
          <TransferAlert
            title="Ownership transfer note"
            message={`This account is currently under the ${
              consumerInfo?.ownershipDisplay?.toLowerCase() === "tenant"
                ? "owner's"
                : "master's"
            } ownership following a completed account transfer to the ${
              consumerInfo?.ownershipDisplay?.toLowerCase() === "tenant"
                ? "owner"
                : "master"
            }.`}
          />
        )}

      {/* Alerts Section */}

      {/* Alerts Section */}
      {/* {consumerInfo.hasAlerts && (
        <ConsumerAlerts alerts={consumerInfo.alerts} />
      )} */}
      {/* Consumer Summary Card */}
      {consumerInfo?.additionalData?.paymentPlanMode === "pay_later" && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-md mb-4 flex items-center shadow-sm">
          <svg
            className="w-5 h-5 mr-2 text-blue-500"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <span className="font-medium">
            NSC charges are not paid for this customer
          </span>
        </div>
      )}
      {/* Consumer Summary Card */}
      <div className="grid grid-cols-4 gap-4">
        <ConsumerInfoCard consumer={consumerInfo} className="col-span-3" />
        <QuickActions
          consumer={consumerInfo}
          className="col-span-1"
          onUpdateDetails={() => setUpdateProfileOpen(true)}
        />
      </div>

      {/* <ConsumerSummaryCard consumer={consumerInfo} /> */}

      {/* Tabs Section */}
      <Tabs
        defaultValue="overview"
        className="w-full"
        tabsListClassName={`grid w-full bg-gradient-to-r from-blue-100/50 to-indigo-100/50 shadow-sm ${
          consumerInfo?.ownershipDisplay === "Master"
            ? "grid-cols-11"
            : "grid-cols-10"
        }`}
        urlMapping={urlMapping}
        tabComponents={tabComponents}
        level={0}
      />
      <UpdateProfileDialog
        isOpen={updateProfileOpen}
        onClose={() => setUpdateProfileOpen(false)}
        consumer={consumerInfo}
        refetchDetails={fetchConsumerData}
      />
    </div>
  );
};

// Sub-component for Consumer Summary Card
interface ConsumerSummaryCardProps {
  consumer: Consumer;
}

interface UpdateProfileDialogProps {
  consumer: Consumer;
  isOpen: boolean;
  onClose: () => void;
  refetchDetails: () => void;
}
const UpdateProfileDialog = ({
  consumer,
  isOpen,
  onClose,
  refetchDetails,
}: UpdateProfileDialogProps) => {
  const { consumerNo } = useParams<{ consumerNo: string }>();
  const { mutate: editConsumerMutation } = useEditActiveConsumer();
  const { data: categoryData } = useCategory();
  const [subCategoryList, setSubCategoryList] = useState<string>("");
  const [selectedMasterAccount, setSelectedMasterAccount] = useState<any>(null);

  const { data: subCategoryData } = useSubCategory(subCategoryList);
  const { data: cities } = useCity();
  const citiesDropdown = mapToLabelValue(cities?.result, "name", "code");
  console.log("debug citues", cities, citiesDropdown);
  const { data: consumerRelationData } = useConsumerRelation();
  const { data: utilityServicesData } = useUtilityServices();
  const relationOptions = mapToLabelValue(
    consumerRelationData?.result,
    "value",
    "key",
  );
  const utilityServicesOptions = mapToLabelValue(
    utilityServicesData,
    "name",
    "name",
  );

  const consumerUtilityServices =
    consumer?.utilityService ||
    consumer?.utilityServices ||
    consumer?.utility_service;
  const { data: planData } = usePlans(consumerUtilityServices);

  const planOptions = useMemo(() => {
    const options =
      planData?.result?.map((plan: any) => ({
        label: plan.planName || plan.name,
        value: plan.id.toString(),
      })) || [];

    // Fallback: Ensure the consumer's current plan is always available in the dropdown
    const currentPlanId =
      consumer?.planDetails?.id?.toString() ||
      consumer?.plan_details?.id?.toString() ||
      consumer?.remotePlanId?.toString() ||
      consumer?.remote_plan_id?.toString();
    if (
      currentPlanId &&
      !options.find((opt: any) => opt.value === currentPlanId)
    ) {
      const currentPlanName =
        consumer?.planDetails?.planName ||
        consumer?.planDetails?.plan_name ||
        consumer?.plan_details?.plan_name ||
        "Current Plan";
      options.push({
        label: currentPlanName,
        value: currentPlanId,
      });
    }

    return options;
  }, [planData, consumer]);

  const { data: ownerTypeData } = useOwnerType();
  const ownerTypeOptions = useMemo(
    () =>
      ownerTypeData?.result?.map((ownerType) => ({
        value: ownerType.key.toString(),
        label: ownerType.value,
      })) || [],
    [ownerTypeData],
  );
  const {
    additionalData = {},
    territoryData,
    addressData,
    ...primaryConsumer
  } = (consumer || {}) as any;

  const defaultValues = {
    ...primaryConsumer,
    hasSecondaryConsumer:
      !!additionalData?.secondaryConsumer?.firstName ||
      !!additionalData?.secondaryConsumer?.first_name,
    secondaryFirstName: additionalData?.secondaryConsumer?.firstName || "",
    secondaryLastName: additionalData?.secondaryConsumer?.lastName || "",
    secondaryPhone: additionalData?.secondaryConsumer?.contactNumber || "",
    secondaryEmail: additionalData?.secondaryConsumer?.email || "",
    houseNo: territoryData?.billing?.unit || "",
    zipCode: territoryData?.billing?.zipcode || "",
    streetAddress: territoryData?.billing?.address || "",
    country: territoryData?.billing?.country || "",
    state: territoryData?.billing?.state || "",
    city: addressData?.billing?.CITY || "",
    premise: territoryData?.billing?.premise || "",
    preferredContactMethod:
      (primaryConsumer as any)?.preferredContactMethod || [],
    tags: (primaryConsumer as any)?.tags || [],
    secondaryRelationship:
      additionalData?.secondaryConsumer?.secondaryRelationship || "",
    category: (primaryConsumer as any)?.category || "",
    subCategory: (primaryConsumer as any)?.subCategory || "",
    isEBill: (primaryConsumer as any)?.isEBill || false,
    isPaperedBill: (primaryConsumer as any)?.isPaperedBill || false,
    ssnTaxId: primaryConsumer?.ssnEncrypted || "",
    dateOfBirth: primaryConsumer?.dateOfBirth || "",
    accountType:
      consumer?.ownership?.toString() ||
      (consumer?.ownershipDetails?.structureChoice === "Sub_account"
        ? "1"
        : consumer?.ownershipDetails?.structureChoice === "Tenant"
          ? "4"
          : consumer?.ownershipDetails?.structureChoice === "Master"
            ? "0"
            : "2"),
    ownerName:
      consumer?.ownershipDetails?.ownerName ||
      consumer?.ownershipDetails?.approvalDetails?.ownerName ||
      consumer?.ownershipDetails?.ownershipDetail?.ownerName ||
      "",
    ownerPhone:
      consumer?.ownershipDetails?.ownerPhone ||
      consumer?.ownershipDetails?.approvalDetails?.ownerPhone ||
      consumer?.ownershipDetails?.ownershipDetail?.ownerPhone ||
      "",
    ownerEmail:
      consumer?.ownershipDetails?.ownerEmail ||
      consumer?.ownershipDetails?.approvalDetails?.ownerEmail ||
      consumer?.ownershipDetails?.ownershipDetail?.ownerEmail ||
      "",
    ownerType:
      consumer?.ownershipDetails?.ownerType ||
      consumer?.ownershipDetails?.approvalDetails?.ownerType ||
      consumer?.ownershipDetails?.ownershipDetail?.ownerType ||
      "",
    ownerAddress:
      consumer?.ownershipDetails?.ownerAddress ||
      consumer?.ownershipDetails?.approvalDetails?.ownerAddress ||
      consumer?.ownershipDetails?.ownershipDetail?.ownerAddress ||
      "",
    utilityServices: (primaryConsumer as any)?.utilityService || [],
    plan: consumer?.remotePlanId?.toString() || "",
  };

  const categoryOptions = useMemo(
    () =>
      categoryData?.result?.map((category) => ({
        value: category.code,
        label: category.name,
      })) || [],
    [categoryData],
  );

  const subCategoryOptions = useMemo(
    () =>
      subCategoryData?.result?.map((subCategory) => ({
        value: subCategory.code,
        label: subCategory.name,
      })) || [],
    [subCategoryData],
  );
  const fields: FormField[] = [
    {
      name: "firstName",
      label: "First Name",
      type: "text",
      required: true,
      group: "Consumer Details",
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "lastName",
      label: "Last Name",
      type: "text",
      required: true,
      group: "Consumer Details",
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "contactNumber",
      label: "Phone No.",
      type: "country-phone",
      required: true,
      group: "Consumer Details",
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "email",
      label: "Email",
      type: "email",
      required: true,
      group: "Consumer Details",
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },

    {
      name: "category",
      label: "Category",
      type: "select",
      options: categoryOptions,
      required: false,
      group: "Consumer Details",
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },

    {
      name: "subCategory",
      label: "Sub-category",
      type: "select",
      options: subCategoryOptions,
      disabled: !subCategoryList,
      required: false,
      group: "Consumer Details",
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "accountType",
      label: "Account Type",
      type: "select",
      options: ownerTypeOptions,
      required: true,
      group: "Account Configuration",
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "plan",
      label: "Plan",
      type: "select",
      options: planOptions,
      group: "Account Configuration",
      groupBorderNeeded: true,
      groupColorNeeded: false,
      className: "col-span-6",
    },
    {
      name: "masterAccount",
      label: "Search master account",
      type: "custom",
      groupBorderNeeded: true,
      groupColorNeeded: false,
      showWhen: (values) => values.accountType === "1",
      group: "Owner Details",
      className: "col-span-12",
      customRender: (field, form) => {
        const remoteUtilityId = useRemoteUtilityId();
        const [isSearching, setIsSearching] = useState(false);
        const [results, setResults] = useState<any[]>([]);
        const [searchTerm, setSearchTerm] = useState("");

        const handleSearch = async (term: string) => {
          setSearchTerm(term);
          if (term.length < 3) {
            setResults([]);
            return;
          }
          setIsSearching(true);
          try {
            const response = await CXCxOnboardingApi.searchMasterConsumer({
              remoteUtilityId: remoteUtilityId.toString(),
              searchData: term,
            });
            setResults(response?.result || []);
          } catch (error) {
            console.error("Master account search failed", error);
            toast({ title: "Search failed", variant: "destructive" });
          } finally {
            setIsSearching(false);
          }
        };

        const onSelect = (acc: any) => {
          const fullName =
            `${acc.firstName || ""} ${acc.lastName || ""}`.trim();
          const masterData = {
            accountNumber: acc.consumerNo,
            accountName: fullName,
            subAccountCount: acc.subAccountCount || 0,
            serviceAddress: acc.serviceAddress || "N/A",
            id: acc.id,
          };
          setSelectedMasterAccount(masterData);
          form.setValue("masterAccount", acc.id);
          setResults([]);
          setSearchTerm("");
        };

        return (
          <div className="space-y-4">
            {!selectedMasterAccount ? (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search by account number or name..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                  />
                </div>
                {searchTerm.length >= 3 && (
                  <div className="bg-white border border-gray-200 rounded-md max-h-48 overflow-y-auto shadow-lg absolute z-10 w-full">
                    {isSearching ? (
                      <div className="p-3 text-center text-sm text-gray-500">
                        Searching...
                      </div>
                    ) : results.length > 0 ? (
                      results.map((acc: any) => {
                        const fullName = `${acc.firstName || ""} ${
                          acc.lastName || ""
                        }`.trim();
                        return (
                          <div
                            key={acc.consumerNo || acc.id}
                            onClick={() => onSelect(acc)}
                            className="p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer"
                          >
                            <div className="font-medium text-gray-900">
                              {fullName}
                            </div>
                            <div className="text-sm text-gray-500">
                              #{acc.consumerNo}{" "}
                              {acc.subAccountCount
                                ? `• ${acc.subAccountCount} sub-accounts`
                                : ""}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-3 text-center text-sm text-gray-500">
                        No accounts found
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-md p-4 relative shadow-sm">
                <button
                  onClick={() => {
                    setSelectedMasterAccount(null);
                    form.setValue("masterAccount", "");
                  }}
                  className="absolute top-4 right-4 text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Change
                </button>
                <h4 className="font-semibold text-gray-900 mb-3 border-b border-gray-100 pb-2">
                  Master Account Details
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 mb-1">Account Number</p>
                    <p className="font-medium text-gray-900">
                      {selectedMasterAccount.accountNumber}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Account Name</p>
                    <p className="font-medium text-gray-900">
                      {selectedMasterAccount.accountName}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Attached Sub-accounts</p>
                    <p className="font-medium text-gray-900">
                      {selectedMasterAccount.subAccountCount}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-500 mb-1">Service Address</p>
                    <p className="font-medium text-gray-900">
                      {selectedMasterAccount.serviceAddress}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      },
    },
    {
      name: "ownerName",
      label: "Owner Name",
      type: "text",
      group: "Owner Details",
      placeholder: "Enter Owner Name",
      showWhen: (values) => values.accountType === "4",
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "ownerPhone",
      label: "Owner Phone",
      type: "phone",
      group: "Owner Details",
      placeholder: "Enter Owner Phone",
      showWhen: (values) => values.accountType === "4",
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "ownerEmail",
      label: "Owner Email",
      type: "email",
      placeholder: "Enter Owner Email",
      group: "Owner Details",
      showWhen: (values) => values.accountType === "4",
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "ownerType",
      label: "Owner Type",
      type: "select",
      options: ownerTypeOptions,
      group: "Owner Details",
      showWhen: (values) => values.accountType === "4",
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "ownerAddress",
      label: "Owner Address",
      type: "textarea",
      group: "Owner Details",
      placeholder: "Enter Owner Address",
      showWhen: (values) => values.accountType === "4",
      className: "col-span-12",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "utilityServices",
      label: "Utility Services",
      type: "multi-select",
      group: "Additional Information",
      options: utilityServicesOptions,
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "tags",
      type: "tags",
      label: "Tags",
      group: "Additional Information",
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "preferredContactMethod",
      label: "Preferred Contact Method",
      type: "multi-select",
      group: "Additional Information",
      groupBorderNeeded: true,
      groupColorNeeded: false,
      options: [
        { label: "Email", value: "Email" },
        { label: "Phone", value: "Phone" },
      ],
      placeholder: "Select a preference",
      className: "col-span-6",
    },
    {
      name: "ssnTaxId",
      label: "SSN/Tax ID",
      type: "text",
      group: "Additional Information",
      required: false,
      placeholder: "Enter SSN or Tax ID",
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "billingType",
      label: "Billing Type",
      type: "select",
      group: "Additional Information",
      groupBorderNeeded: true,
      groupColorNeeded: false,
      options: [
        { label: "Email", value: "Email" },
        { label: "Phone", value: "Phone" },
      ],
      placeholder: "Select a preference",
      className: "col-span-6",
    },
    {
      name: "dateOfBirth",
      label: "Date of Birth",
      type: "datepicker",
      group: "Additional Information",
      required: false,
      placeholder: "Enter Date of Birth",
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "isVip",
      label: "VIP Designation",
      type: "checkbox",
      group: "Additional Information",
      className: "col-span-6 mt-[40px]",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },

    {
      name: "hasSecondaryConsumer",
      label: "Add Co Applicant",
      type: "checkbox",
      group: "Co Applicant Details",
      className: "col-span-12",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },

    // ---------- Secondary Consumer (all fields) ----------
    {
      name: "secondaryFirstName",
      label: "Co Applicant's First Name",
      type: "text",
      placeholder: "Enter Co Applicant's first name",
      group: "Co Applicant Details",
      showWhen: (values) => values.hasSecondaryConsumer,
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "secondaryLastName",
      label: "Co Applicant's Last Name",
      type: "text",
      placeholder: "Enter Co Applicant's last name",
      group: "Co Applicant Details",
      showWhen: (values) => values.hasSecondaryConsumer,
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      label: "Relationship to Primary",
      name: "secondaryRelationship",
      type: "select",
      options: relationOptions,
      placeholder: "Select a relationship",
      group: "Co Applicant Details",
      showWhen: (values) => values.hasSecondaryConsumer,
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "secondaryPhone",
      label: "Co Applicant's Phone",
      type: "phone",
      placeholder: "Enter Co Applicant's phone number",
      group: "Co Applicant Details",
      showWhen: (values) => values.hasSecondaryConsumer,
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "secondaryEmail",
      label: "Co Applicant's Email",
      type: "email",
      placeholder: "Enter Co Applicant's email",
      group: "Co Applicant Details",
      showWhen: (values) => values.hasSecondaryConsumer,
      className: "col-span-12",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },

    // ---------- Address Fields ----------
    {
      name: "houseNo",
      label: "House No.",
      type: "text",
      group: "Mailing Address",
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "city",
      label: "City",
      type: "select",
      group: "Mailing Address",
      options: citiesDropdown,
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "zipCode",
      label: "Zip Code",
      type: "text",
      group: "Mailing Address",
      className: "col-span-6",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "isEBill",
      label: "E-bill",
      type: "checkbox",
      group: "Mailing Address",
      className: "col-span-2 mt-[40px]",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "isPaperedBill",
      label: "Paper-bill",
      type: "checkbox",
      group: "Mailing Address",
      className: "col-span-4 mt-[40px]",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
    {
      name: "streetAddress",
      label: "Street Address",
      type: "textarea",
      group: "Mailing Address",
      className: "col-span-12",
      groupBorderNeeded: true,
      groupColorNeeded: false,
    },
  ];

  const updateProfileForm = FormService.createForm({
    fields,
    mode: "onChange",
    validateOnMount: false,
  });

  const handleFieldChange = (fieldName: string, value: any) => {
    if (fieldName === "category") {
      setSubCategoryList(value);
    }
    if (fieldName === "accountType") {
      setSelectedMasterAccount(null);
    }
  };

  const handleSubmit = (data: any) => {
    console.log("debug submit data", data);
    const getRelationshipLabel = () => {
      return (
        relationOptions.find(
          (option) => option.value == data.secondaryRelationship,
        )?.label || ""
      );
    };
    const relationshipLabel = getRelationshipLabel();
    const getCategoryLabel = () => {
      return (
        categoryOptions.find((option) => option.value === data.category)
          ?.label || ""
      );
    };

    const getSubCategoryLabel = () => {
      return (
        subCategoryOptions.find((option) => option.value === data.subCategory)
          ?.label || ""
      );
    };
    const payload = {
      consumer_details: {
        primary_consumer: {
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          is_vip: data.isVip,
          contact_number: data.contactNumber,
          tags: updateProfileForm.getValues("tags"),
          preferred_contact_method: data.preferredContactMethod,
          account_category: data.category || "",
          account_category_display: getCategoryLabel(),
          account_sub_category: data.subCategory || "",
          account_sub_category_display: getSubCategoryLabel(),
          ssnEncrypted: data.ssnTaxId || "",
          dateOfBirth: data.dateOfBirth || "",
          plan: data.plan || "",
          ownership: parseInt(data.accountType),
          utility_service: data.utilityServices,
          approval_details:
            data.accountType === "1" && selectedMasterAccount
              ? {
                  master_account_id: selectedMasterAccount.id,
                  master_account_no: selectedMasterAccount.accountNumber,
                  master_account_name: selectedMasterAccount.accountName,
                  approved_at: new Date().toISOString(),
                }
              : data.accountType === "4"
                ? {
                    owner_name: data.ownerName,
                    owner_phone: data.ownerPhone,
                    owner_email: data.ownerEmail || null,
                    owner_type: data.ownerType,
                    owner_address: data.ownerAddress,
                  }
                : null,
        },
        secondary_consumer: data.hasSecondaryConsumer
          ? {
              first_name: data.secondaryFirstName,
              last_name: data.secondaryLastName,
              email: data.secondaryEmail,
              contact_number: data.secondaryPhone,
              secondary_relationship: data.secondaryRelationship,
              secondary_consumer_display: relationshipLabel,
            }
          : {},
      },
      billing_address_data: {
        zipcode: data.zipCode,
        city: data.city,
        unit: data.houseNo,
        address: data.streetAddress,
      },
      is_papered_bill: updateProfileForm.getValues("isPaperedBill"),
      is_e_bill: updateProfileForm.getValues("isEBill"),
      remote_utility_id: getRemoteUtilityId(),
      source: "0",
      remotePlanId: data.plan,
      completePlanData:
        planData?.result?.find((p: any) => p.id.toString() === data.plan) ||
        null,
    };

    console.warn("payload", JSON.stringify(payload));

    editConsumerMutation(
      {
        consumerId: consumer?.id,
        payload,
      },
      {
        onSuccess: () => {
          refetchDetails();
          onClose();
        },
      },
    );
  };
  useEffect(() => {
    if (consumer) {
      FormService.populateForm(updateProfileForm, defaultValues, fields);

      // Initialize selectedMasterAccount if sub-account
      if (consumer.ownershipDetails?.structureChoice === "Sub_account") {
        const details = consumer.ownershipDetails;
        setSelectedMasterAccount({
          accountNumber:
            details.masterAccountEmail ||
            details.approvalDetails?.masterAccountEmail ||
            "N/A",
          accountName:
            details.masterAccountName ||
            details.approvalDetails?.masterAccountName ||
            "N/A",
          subAccountCount:
            details.subAccountCount ||
            details.approvalDetails?.subAccountCount ||
            0,
          serviceAddress:
            details.serviceAddress ||
            details.approvalDetails?.serviceAddress ||
            "N/A",
          id:
            details.masterAccountId ||
            details.approvalDetails?.masterAccountId ||
            0,
        });
      }
    }
  }, [consumer]);

  useEffect(() => {
    if (planData?.result && defaultValues.plan) {
      const currentPlan = updateProfileForm.getValues("plan");

      // Force set the value to make sure the UI picks it up
      updateProfileForm.setValue("plan", defaultValues.plan, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [planData, defaultValues.plan, planOptions]);
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="min-w-[60vw] max-h-[80vh] overflow-scroll">
        <DialogDescription>Update Consumer Profile</DialogDescription>
        <DynamicForm
          fields={fields}
          form={updateProfileForm}
          onFieldChange={handleFieldChange}
          onSubmit={(values) => {
            handleSubmit(values);
          }}
          onReset={() => {
            onClose();
          }}
          gridColumns={12}
          config={{
            classes: BUTTON_FIELD_CLASSES,
            showSubmitButton: true,
            showResetButton: true,
            submitButtonText: "Update",
            resetButtonText: "Cancel",
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ConsumerDetail;
